import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { AddRequestAction, DeleteRequestAction, FetchRequestAction, ReportActionTypes, StrapiActionTypes, UpdateRequestAction } from '../types/types';
import { FetchDataMainParams } from '../types/request';
import { addStrapiData, deleteStrapiData, deleteFileFromStrapi, extractStrapiData, fetchStrapiData, updateStrapiData } from '../api/strapiGop';
import {
  addDataFailure,
  addDataSuccess,
  deleteDataFailure,
  deleteDataSuccess,
  fetchData,
  fetchDataFailure,
  fetchDataSuccess,
  setLastAddedId,
  setLastDocId,
  updateDataFailure,
  updateDataSuccess,
} from '../actions/actions';
import { selectModelData } from '../selectors/selectors';
import { fetchDataMainRequest, createAnswerRequest, updateAnswerRequest, createQuestionnaireRequest, updateQuestionnaireRequest, fetchCommentsOnlySuccess, fetchCommentsOnlyRequest, fetchActionsOnlySuccess, fetchActionsOnlyRequest } from '../store/data-main/data-main';
import { fetchDataSummaryRequest } from '../store/data-summary/data-summary';
import { fetchReportFactoryRequest } from '../store/report-factory/report-factory';
import { fetchFiltersRequest } from '../store/filters/filters';
import { fetchSettingsRequest } from '../store/settings/settings';
import { fetchDirectionsRequest } from '../store/directions/directions';
import { fetchCurrentDateRequest } from '../store/date/date';
import { handleFetchDataMain, handleCreateAnswer, handleUpdateAnswer, handleCreateQuestionnaire, handleUpdateQuestionnaire } from '../store/data-main/saga';
import { handleFetchDataSummary } from '../store/data-summary/saga';
import { handleFetchReportFactory } from '../store/report-factory/saga';
import { handleFetchFilters } from '../store/filters/saga';
import { handleFetchSettings } from '../store/settings/saga';
import { handleFetchDirections } from '../store/directions/saga';
import { handleFetchCurrentDate } from '../store/date/saga';
import { fetchReportDataSaga } from '../store/reports/saga';
import { fetchCommentByContextSaga, uploadFileSaga } from '../store/comments-files/saga';
import { fetchActionByContextSaga, fetchLinkedActionsSaga } from '../store/link-actions/saga';
import { fetchKPIDataSaga, fetchKPIChartDataSaga } from '../store/report-kpi/saga';
import { FETCH_KPI_DATA_REQUEST, FETCH_KPI_CHART_DATA_REQUEST } from '../store/report-kpi/actions';
import * as api from '../api/api';

function* fetchDataSaga(action: FetchRequestAction) {
  const { modelName, params, pagination } = action.payload;
  try {
    const response: AxiosResponse<any> = yield call(fetchStrapiData, modelName, params);
    const extractedData = extractStrapiData(response);
    const paginationInfo = response.data?.meta?.pagination;
    yield put(fetchDataSuccess(modelName, extractedData, paginationInfo));
  } catch (error) {
    const errorMessage = (error as any)?.message || '';
    yield put(fetchDataFailure(modelName, errorMessage));
  }
}

function* addDataSaga(action: AddRequestAction) {
  const { modelName, data } = action.payload;
  try {
    const response: AxiosResponse<any> = yield call(addStrapiData, modelName, data);
    const extractedData = extractStrapiData(response);
    yield put(addDataSuccess(modelName, extractedData));
    yield put(setLastAddedId(modelName, extractedData.id));
    if (extractedData.documentId) {
      yield put(setLastDocId(modelName, extractedData.documentId));
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || '';
    yield put(addDataFailure(modelName, errorMessage));
  }
}

function* addDataSuccessSaga(action: AddRequestAction) {
  const { modelName } = action.payload;
  yield put(fetchData(modelName));
}

function* deleteDataSaga(action: DeleteRequestAction) {
  const { modelName, id } = action.payload;
  try {
    if (modelName === 'gop-uploads') {
      const currentData = yield select((state) => selectModelData(state, 'gop-uploads'));
      const recordToDelete = currentData.find((item: any) => item.documentId === id);
      if (recordToDelete?.Docs) {
        try {
          if (Array.isArray(recordToDelete.Docs)) {
            for (const doc of recordToDelete.Docs) {
              if (doc?.id) {
                yield call(deleteFileFromStrapi, doc.id);
              }
            }
          } else if (recordToDelete.Docs?.id) {
            yield call(deleteFileFromStrapi, recordToDelete.Docs.id);
          }
        } catch (fileError) {
          console.log('Ошибка при удалении:', fileError);
        }
      }
    }
    yield call(deleteStrapiData, modelName, id);
    yield put(deleteDataSuccess(modelName, id));
    yield put(fetchData('gop-uploads'));
  } catch (error) {
    const errorMessage = (error as any)?.response?.data?.error?.message || (error as any)?.message || 'Ошибка при удалении записи';
    yield put(deleteDataFailure(modelName, errorMessage));
  }
}

function* updateDataSaga(action: UpdateRequestAction) {
  const { modelName, documentId, data } = action.payload;
  try {
    const response: AxiosResponse<any> = yield call(updateStrapiData, modelName, documentId, data);
    const extractedData = extractStrapiData(response);
    yield put(updateDataSuccess(modelName, extractedData));
    yield put(fetchData(modelName));
  } catch (error) {
    console.error('Ошибка при обновлении:', error);
    const errorMessage = (error as any)?.message || 'Ошибка при обновлении записи';
    yield put(updateDataFailure(modelName, errorMessage));
  }
}

function* fetchCommentsOnlySaga(action: PayloadAction<FetchDataMainParams>) {
  try {
    const response: { comments: any[] } = yield call(api.getCommentsOnly, action.payload);
    yield put(fetchCommentsOnlySuccess(response));
  } catch (error) {
    console.error('Ошибка загрузки комментариев:', error);
  }
}

function* fetchActionsOnlySaga(action: PayloadAction<FetchDataMainParams>) {
  try {
    const response: { actions: any[] } = yield call(api.getActionsOnly, action.payload);
    yield put(fetchActionsOnlySuccess(response));
  } catch (error) {
    console.error('Ошибка загрузки действий:', error);
    yield put(fetchActionsOnlySuccess({ actions: [] }));
  }
}

export function* gopWatcher() {
  yield takeEvery(StrapiActionTypes.FETCH_REQUEST, fetchDataSaga);
  yield takeEvery(StrapiActionTypes.ADD_REQUEST, addDataSaga);
  yield takeEvery(StrapiActionTypes.ADD_SUCCESS, addDataSuccessSaga);
  yield takeEvery(StrapiActionTypes.DELETE_REQUEST, deleteDataSaga);
  yield takeEvery(StrapiActionTypes.UPLOAD_FILE_REQUEST, uploadFileSaga);
  yield takeEvery(StrapiActionTypes.UPDATE_REQUEST, updateDataSaga);
  yield takeEvery(ReportActionTypes.FETCH_REPORT_DATA_REQUEST, fetchReportDataSaga);
  // yield takeEvery(ReportActionTypes.FETCH_REPORT_ACTIONS_REQUEST, fetchReportActionsSaga);
  yield takeEvery(ReportActionTypes.FETCH_LINKED_ACTIONS_REQUEST, fetchLinkedActionsSaga);
  yield takeEvery(StrapiActionTypes.FETCH_ACTION_BY_CONTEXT_REQUEST, fetchActionByContextSaga);
  yield takeEvery(StrapiActionTypes.FETCH_COMMENT_BY_CONTEXT_REQUEST, fetchCommentByContextSaga);
  yield takeEvery(fetchCommentsOnlyRequest.type, fetchCommentsOnlySaga);
  yield takeEvery(fetchActionsOnlyRequest.type, fetchActionsOnlySaga);
  yield takeEvery(FETCH_KPI_DATA_REQUEST, fetchKPIDataSaga);
  yield takeEvery(FETCH_KPI_CHART_DATA_REQUEST, fetchKPIChartDataSaga);
  yield takeLatest(fetchDataMainRequest.type, handleFetchDataMain);
  yield takeLatest(fetchFiltersRequest.type, handleFetchFilters);
  yield takeLatest(fetchSettingsRequest.type, handleFetchSettings);
  yield takeLatest(fetchDataMainRequest.type, handleFetchDataMain);
  yield takeLatest(fetchDataSummaryRequest.type, handleFetchDataSummary);
  yield takeLatest(fetchReportFactoryRequest.type, handleFetchReportFactory);
  yield takeLatest(fetchDirectionsRequest.type, handleFetchDirections);
  yield takeLatest(fetchCurrentDateRequest.type, handleFetchCurrentDate);
  yield takeLatest(createAnswerRequest.type, handleCreateAnswer);
  yield takeLatest(updateAnswerRequest.type, handleUpdateAnswer);
  yield takeLatest(createQuestionnaireRequest.type, handleCreateQuestionnaire);
  yield takeLatest(updateQuestionnaireRequest.type, handleUpdateQuestionnaire);
}
